import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, Navigation, Clock, MapPin, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/services/api';
import useAnimatedPosition from '@/hooks/useAnimatedPosition';

// Ícones (mesma identidade visual do MapaEntrega)
const buildIconeMoto = (rotationDeg = 0) => L.divIcon({
  className: 'custom-bike-icon-nav',
  html: `<div style="background:#ea580c;border-radius:50%;width:48px;height:48px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(234,88,12,0.5);border:3px solid white;">
    <span style="display:flex;transform:rotate(${rotationDeg}deg);transition:transform 1s ease-out;">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d="M19 12c-1.49 0-2.87.47-4 1.27V11l4-4h-3V5h-2v2h-3l-2 2H7c-1.66 0-3 1.34-3 3v3l5 1v3l3-3-2.59-2.59c-.78-.78-.78-2.05 0-2.83.78-.78 2.05-.78 2.83 0L14 11c.93-1.21 2.39-2 4-2 2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5h2c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3z"/></svg>
    </span>
  </div>`,
  iconSize: [48, 48],
  iconAnchor: [24, 24],
});

const iconeDestino = L.divIcon({
  html: `<div style="background:#dc2626;border-radius:50% 50% 50% 0;width:36px;height:36px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white" style="transform:rotate(45deg);"><path d="M12 2L2 12h3v8h6v-6h2v6h6v-8h3L12 2z"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const FitBounds = ({ pontos }) => {
  const map = useMap();
  useEffect(() => {
    const validos = pontos.filter(p => p && p[0] && p[1]);
    if (validos.length === 0) return;
    if (validos.length === 1) {
      map.setView(validos[0], 16);
    } else {
      map.fitBounds(validos, { padding: [60, 60] });
    }
  }, [pontos, map]);
  return null;
};

/**
 * NavegacaoInApp
 * Modal full-screen com navegação ao destino (loja ou cliente).
 * - Mostra rota traçada via Geoapify (com fallback se sem key)
 * - Posição GPS em tempo real (animada + rotacionada)
 * - ETA + distância restante
 * - Recalcula rota a cada 60s ou manualmente
 * - Botões fallback: Waze + Google Maps
 *
 * Props:
 * - destino: { lat, lng, endereco, label }
 * - posicaoAtual: [lat, lng] do GPS do entregador (vem de useGeolocation no parent)
 * - onClose: () => void
 */
const NavegacaoInApp = ({ destino, posicaoAtual, onClose }) => {
  const [rota, setRota] = useState(null);
  const [loadingRota, setLoadingRota] = useState(false);
  const ultimoRecalculoRef = useRef(0);
  const intervalRef = useRef(null);

  // Posição animada + bearing do entregador
  const { position: posAnimada, bearing } = useAnimatedPosition(posicaoAtual, 1500);
  const iconeMoto = useMemo(() => buildIconeMoto(bearing - 90), [bearing]);

  // Calcula rota inicial e recalcula a cada 60s
  useEffect(() => {
    if (!destino || !posicaoAtual) return;

    const calcular = async () => {
      const agora = Date.now();
      if (agora - ultimoRecalculoRef.current < 5000) return; // throttle 5s mínimo
      ultimoRecalculoRef.current = agora;
      setLoadingRota(true);
      try {
        const res = await api.post('/navegacao/rota', {
          origem_lat: posicaoAtual[0],
          origem_lng: posicaoAtual[1],
          destino_lat: destino.lat,
          destino_lng: destino.lng,
        });
        setRota(res.data);
      } catch {
        toast.error('Erro ao calcular rota');
      } finally {
        setLoadingRota(false);
      }
    };

    calcular();
    intervalRef.current = setInterval(calcular, 60000); // 60s
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    // Recalcula quando destino muda; posicaoAtual atualiza dentro do interval
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destino?.lat, destino?.lng]);

  const recalcularManual = async () => {
    ultimoRecalculoRef.current = 0;
    if (!posicaoAtual || !destino) return;
    setLoadingRota(true);
    try {
      const res = await api.post('/navegacao/rota', {
        origem_lat: posicaoAtual[0],
        origem_lng: posicaoAtual[1],
        destino_lat: destino.lat,
        destino_lng: destino.lng,
      });
      setRota(res.data);
      toast.success('Rota atualizada');
    } catch {
      toast.error('Erro ao recalcular');
    } finally {
      setLoadingRota(false);
    }
  };

  const abrirWaze = () => {
    const url = `https://waze.com/ul?ll=${destino.lat},${destino.lng}&navigate=yes`;
    window.open(url, '_blank');
  };
  const abrirGoogleMaps = () => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destino.lat},${destino.lng}`;
    window.open(url, '_blank');
  };

  const destinoLatLng = destino ? [destino.lat, destino.lng] : null;
  const pontos = [posAnimada, destinoLatLng].filter(Boolean);

  if (!destino) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-white flex flex-col"
      data-testid="navegacao-in-app"
    >
      {/* Header fixo no topo */}
      <header className="bg-gradient-to-r from-orange-600 to-amber-500 text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-orange-100 uppercase tracking-wide font-semibold">
            Navegando para {destino.label || 'destino'}
          </p>
          <p className="text-sm font-medium truncate">{destino.endereco || '—'}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-full"
          data-testid="btn-fechar-navegacao"
        >
          <X className="w-6 h-6" />
        </button>
      </header>

      {/* Card de ETA flutuante */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <div>
            <p className="text-xs text-slate-500">ETA</p>
            <div className="flex items-baseline gap-1">
              <Clock className="w-4 h-4 text-orange-600" />
              <p className="text-2xl font-bold text-slate-900" data-testid="nav-eta">
                {rota?.duracao_min != null ? rota.duracao_min : '—'}
              </p>
              <span className="text-xs text-slate-500">min</span>
            </div>
          </div>
          <div className="border-l border-slate-200 pl-4">
            <p className="text-xs text-slate-500">Distância</p>
            <div className="flex items-baseline gap-1">
              <MapPin className="w-4 h-4 text-orange-600" />
              <p className="text-2xl font-bold text-slate-900" data-testid="nav-dist">
                {rota?.distancia_km != null ? rota.distancia_km.toFixed(1) : '—'}
              </p>
              <span className="text-xs text-slate-500">km</span>
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={recalcularManual}
          disabled={loadingRota || !posicaoAtual}
          className="text-orange-600 hover:bg-orange-50"
          data-testid="btn-recalcular-rota"
          title="Recalcular rota"
        >
          {loadingRota ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mapa (flex-1) */}
      <div className="flex-1 relative bg-slate-100">
        {!posicaoAtual ? (
          <div className="absolute inset-0 flex items-center justify-center text-center px-6">
            <div>
              <Loader2 className="w-10 h-10 text-orange-600 animate-spin mx-auto mb-3" />
              <p className="font-semibold text-slate-700">Aguardando GPS…</p>
              <p className="text-xs text-slate-500 mt-1">
                Permita o acesso à localização para iniciar a navegação.
              </p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={posAnimada || destinoLatLng}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap contributors"
            />
            {posAnimada && (
              <Marker position={posAnimada} icon={iconeMoto}>
                <Popup>Você está aqui</Popup>
              </Marker>
            )}
            {destinoLatLng && (
              <Marker position={destinoLatLng} icon={iconeDestino}>
                <Popup>{destino.endereco}</Popup>
              </Marker>
            )}
            {rota?.polyline && rota.polyline.length > 1 && (
              <Polyline
                positions={rota.polyline}
                pathOptions={{ color: '#ea580c', weight: 6, opacity: 0.85 }}
              />
            )}
            <FitBounds pontos={pontos} />
          </MapContainer>
        )}
      </div>

      {/* Footer com botões fallback */}
      <div className="bg-white border-t border-slate-200 px-4 py-3">
        <p className="text-xs text-slate-500 text-center mb-2">
          Prefere usar outro app de navegação?
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            onClick={abrirWaze}
            className="border-blue-400 text-blue-600 hover:bg-blue-50"
            data-testid="btn-abrir-waze"
          >
            <ExternalLink className="w-4 h-4 mr-2" /> Waze
          </Button>
          <Button
            variant="outline"
            onClick={abrirGoogleMaps}
            className="border-green-500 text-green-600 hover:bg-green-50"
            data-testid="btn-abrir-gmaps"
          >
            <Navigation className="w-4 h-4 mr-2" /> Google Maps
          </Button>
        </div>
        {rota?.provider === 'fallback' && (
          <p className="text-[10px] text-amber-700 text-center mt-2 leading-tight">
            ⚠ Rota em linha reta (Geoapify não configurado). Para navegação real,
            configure GEOAPIFY_API_KEY.
          </p>
        )}
      </div>
    </div>
  );
};

export default NavegacaoInApp;
